import React, { useState, useEffect } from 'react';
import {
    FaTimes,
    FaClipboardList,
    FaSave,
    FaTrash,
    FaFlag
} from 'react-icons/fa';
import styles from './EditOrderModal.module.css';
import orderService from '../../services/orderService';
import { useAccessToken, useLockBodyScroll } from '../../hooks';
import { useModalAlert } from '../../hooks/useModalAlert';
import { getOrderStatus, ORDER_STATUS_LABELS } from '../../types/orders.types';
import ModalAlert from './ModalAlert';

/**
 * Edit Order Modal Component
 * Modal for managing an existing order (view, edit, delete)
 */
const EditOrderModal = ({ 
    order, 
    isOpen, 
    onClose, 
    onOrderUpdated 
}) => {
    const { getBackendAccessToken } = useAccessToken();
    const { modalAlert, setInfoAlert, setErrorAlert, clearAlert } = useModalAlert();

    // Lock body scroll when modal is open
    useLockBodyScroll(isOpen);

    // Form state
    const [formData, setFormData] = useState({
        orderDate: '',
        processedDate: '',
        completedDate: '',
        processed: false,
        completed: false,
        reviewFlag: false
    });

    // UI state
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [step, setStep] = useState(1); // 1: Order Info, 2: Actions

    /**
     * Initialize form data when order changes
     */
    useEffect(() => {
        if (order && isOpen) {
            setFormData({
                orderDate: order.orderDate || '',
                processedDate: order.processedDate || '',
                completedDate: order.completedDate || '',
                processed: order.processed || false,
                completed: order.completed || false,
                reviewFlag: order.reviewFlag || false
            });
            setErrors({});
            setStep(1);
            clearAlert();
        }
    }, [order, isOpen, clearAlert]);

    /**
     * Handle input changes
     */
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    /**
     * Validate form data
     */
    const validate = () => {
        const newErrors = {};

        if (!formData.orderDate) {
            newErrors.orderDate = 'Order date is required';
        }

        return newErrors;
    };

    /**
     * Handle form submission
     */
    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        try {
            setSaving(true);
            setErrors({});
            
            const accessToken = await getBackendAccessToken();
            
            // Update order
            const updatedOrder = await orderService.updateOrder(
                accessToken,
                order.orderId,
                formData
            );

            setInfoAlert('Order updated successfully');
            
            // Notify parent component
            if (onOrderUpdated) {
                onOrderUpdated(updatedOrder);
            }

            // Stay on current step to allow more actions
        } catch (err) {
            console.error('Failed to update order:', err);
            setErrorAlert(err.message || 'Failed to update order');
        } finally {
            setSaving(false);
        }
    };

    /**
     * Handle delete
     */
    const handleDelete = async () => {
        const confirmMessage = `Are you sure you want to delete order "${order.orderId}"? This action cannot be undone.`;
        
        if (!window.confirm(confirmMessage)) {
            return;
        }

        try {
            setSaving(true);
            const accessToken = await getBackendAccessToken();
            await orderService.deleteOrder(accessToken, order.orderId);
            
            setInfoAlert('Order deleted successfully');
            
            // Notify parent and close
            if (onOrderUpdated) {
                onOrderUpdated(null); // null indicates deletion
            }
            
            handleClose();
        } catch (err) {
            console.error('Failed to delete order:', err);
            setErrorAlert(err.message || 'Failed to delete order');
        } finally {
            setSaving(false);
        }
    };

    /**
     * Handle next step
     */
    const handleNext = () => {
        setStep(2);
    };

    /**
     * Handle previous step
     */
    const handlePrevious = () => {
        setStep(1);
    };

    /**
     * Handle modal close
     */
    const handleClose = () => {
        setStep(1);
        clearAlert();
        onClose();
    };

    /**
     * Format date for display
     */
    const formatDate = (dateString) => {
        if (!dateString) return 'Not set';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Don't render if not open or no order
    if (!isOpen || !order) return null;

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                {/* Modal Header */}
                <div className={styles.modalHeader}>
                    <div className={styles.headerInfo}>
                        <div className={styles.headerIcon}>
                            <FaClipboardList />
                        </div>
                        <div>
                            <h2 className={styles.modalTitle}>
                                Manage Order
                            </h2>
                            <p className={styles.modalSubtitle}>
                                {order.orderId} - {order.clientName}
                            </p>
                        </div>
                    </div>
                    <button
                        className={styles.closeButton}
                        onClick={handleClose}
                        title="Close"
                        disabled={saving}
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Modal Alert - Shows inside the modal */}
                <ModalAlert
                    alert={modalAlert}
                    onClose={clearAlert}
                    autoHideDuration={5000}
                />

                {/* Step 1 - Order Information */}
                {step === 1 && (
                    <form onSubmit={handleSubmit} className={styles.form}>
                        {/* Step Indicator */}
                        <div className={styles.stepIndicator}>
                            <div className={`${styles.step} ${styles.active}`}>
                                <div className={styles.stepNumber}>1</div>
                                <div className={styles.stepLabel}>Order Info</div>
                            </div>
                            <div className={styles.stepLine}></div>
                            <div className={styles.step}>
                                <div className={styles.stepNumber}>2</div>
                                <div className={styles.stepLabel}>Actions</div>
                            </div>
                        </div>

                        <div className={styles.formStep}>
                            <h3 className={styles.sectionTitle}>
                                <FaClipboardList /> Order Information
                            </h3>

                            {errors.general && (
                                <div className={styles.errorMessage}>{errors.general}</div>
                            )}

                            {/* Order ID (Read-only) */}
                            <div className={styles.formGroup}>
                                <label className={styles.label}>Order ID</label>
                                <input
                                    type="text"
                                    className={styles.input}
                                    value={order.orderId}
                                    disabled
                                />
                            </div>

                            {/* Client (Read-only) */}
                            <div className={styles.formGroup}>
                                <label className={styles.label}>Client</label>
                                <input
                                    type="text"
                                    className={styles.input}
                                    value={`${order.clientName} (${order.clientCode})`}
                                    disabled
                                />
                            </div>

                            {/* Status (Read-only) */}
                            <div className={styles.formGroup}>
                                <label className={styles.label}>Status</label>
                                <input
                                    type="text"
                                    className={styles.input}
                                    value={ORDER_STATUS_LABELS[getOrderStatus(order)]}
                                    disabled
                                />
                            </div>

                            {/* Order Date */}
                            <div className={styles.formGroup}>
                                <label htmlFor="orderDate" className={styles.label}>
                                    Order Date <span className={styles.required}>*</span>
                                </label>
                                <input
                                    type="date"
                                    id="orderDate"
                                    name="orderDate"
                                    className={`${styles.input} ${errors.orderDate ? styles.inputError : ''}`}
                                    value={formData.orderDate}
                                    onChange={handleChange}
                                    disabled={saving}
                                />
                                {errors.orderDate && (
                                    <span className={styles.errorMessage}>{errors.orderDate}</span>
                                )}
                            </div>

                            {/* Processed Date */}
                            <div className={styles.formGroup}>
                                <label htmlFor="processedDate" className={styles.label}>
                                    Processed Date
                                </label>
                                <input
                                    type="date"
                                    id="processedDate"
                                    name="processedDate"
                                    className={styles.input}
                                    value={formData.processedDate}
                                    onChange={handleChange}
                                    disabled={saving}
                                />
                            </div>

                            {/* Completed Date */}
                            <div className={styles.formGroup}>
                                <label htmlFor="completedDate" className={styles.label}>
                                    Completed Date
                                </label>
                                <input
                                    type="date"
                                    id="completedDate"
                                    name="completedDate"
                                    className={styles.input}
                                    value={formData.completedDate}
                                    onChange={handleChange}
                                    disabled={saving}
                                />
                            </div>

                            {/* Processed Checkbox */}
                            <div className={styles.checkboxGroup}>
                                <label className={styles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        name="processed"
                                        checked={formData.processed}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                    <span>Samples Processed</span>
                                </label>
                            </div>

                            {/* Completed Checkbox */}
                            <div className={styles.checkboxGroup}>
                                <label className={styles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        name="completed"
                                        checked={formData.completed}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                    <span>Order Completed</span>
                                </label>
                            </div>

                            {/* Review Flag */}
                            <div className={styles.checkboxGroup}>
                                <label className={styles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        name="reviewFlag"
                                        checked={formData.reviewFlag}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                    <span>
                                        <FaFlag /> Flag for review
                                    </span>
                                </label>
                            </div>
                        </div>

                        {/* Form Actions */}
                        <div className={styles.formActions}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleClose}
                                disabled={saving}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className={styles.saveButton}
                                disabled={saving}
                            >
                                <FaSave />
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                            <button
                                type="button"
                                className={styles.nextButton}
                                onClick={handleNext}
                                disabled={saving}
                            >
                                Next: Actions →
                            </button>
                        </div>
                    </form>
                )}

                {/* Step 2 - Actions */}
                {step === 2 && (
                    <div className={styles.actionsStep}>
                        <div className={styles.formStep}>
                            <h3 className={styles.sectionTitle}>
                                Order Actions
                            </h3>

                            <p className={styles.helpText}>
                                Manage this order with the available actions below.
                            </p>

                            {/* Delete Order */}
                            <div className={styles.actionsList}>
                                <div className={styles.actionCard}>
                                    <div className={`${styles.actionIcon} ${styles.danger}`}>
                                        <FaTrash />
                                    </div>
                                    <div className={styles.actionInfo}>
                                        <h4 className={styles.actionTitle}>Delete Order</h4>
                                        <p className={styles.actionDescription}>
                                            Permanently remove this order from the system. This action cannot be undone.
                                        </p>
                                        <button
                                            type="button"
                                            className={`${styles.actionButton} ${styles.danger}`}
                                            onClick={handleDelete}
                                            disabled={saving}
                                        >
                                            Delete Order
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Form Actions */}
                        <div className={styles.formActions}>
                            <button
                                type="button"
                                className={styles.backButton}
                                onClick={handlePrevious}
                                disabled={saving}
                            >
                                ← Back to Order Info
                            </button>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleClose}
                                disabled={saving}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EditOrderModal;
